/**
 * Jakarta time (WIB, UTC+7) is the warehouse's clock.
 *
 * The backend stores and returns instants in UTC, and the browser's own
 * `format`/`toLocaleString` render them in whatever zone the viewer's machine
 * happens to be set to. So the same receipt read "12:05" on a laptop in
 * Jakarta and "05:05" on one set to UTC, and a printed label or exported PDF
 * carried whichever zone the person who clicked Export was in. Stock moves,
 * shift handovers and audit trails are all reasoned about in WIB, so every
 * date and time shown to a user goes through this module and reads as Jakarta
 * regardless of the machine. Instants sent back to the API are untouched —
 * they stay UTC.
 *
 * Implementation note: `Intl.DateTimeFormat(...).formatToParts()` is the only
 * source of truth for the conversion (it owns the tz database); the parts are
 * then carried in a `Date` subclass whose local getters return UTC values, so
 * date-fns `format` reads the Jakarta wall clock straight off it. That keeps
 * every call site's existing date-fns pattern string working, and — because
 * UTC has no DST — the carrier can always represent the wall clock exactly,
 * which a plain local-field `new Date(y, m, d, h, …)` cannot.
 */

import { format as formatDateFns } from 'date-fns'

export const JAKARTA_TIME_ZONE = 'Asia/Jakarta'

export type DateTimeValue = Date | string | number | null | undefined

/** A bare business date — `2026-10-03` — with no instant behind it. */
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Carries a Jakarta wall clock for display. Its local getters answer with UTC
 * values, which is what date-fns `format` reads. Never send one to the API or
 * treat its `getTime()` as a real instant — it is a display vehicle only.
 */
class JakartaWallClock extends Date {
  getFullYear() {
    return this.getUTCFullYear()
  }
  getMonth() {
    return this.getUTCMonth()
  }
  getDate() {
    return this.getUTCDate()
  }
  getDay() {
    return this.getUTCDay()
  }
  getHours() {
    return this.getUTCHours()
  }
  getMinutes() {
    return this.getUTCMinutes()
  }
  getSeconds() {
    return this.getUTCSeconds()
  }
  getMilliseconds() {
    return this.getUTCMilliseconds()
  }
  getTimezoneOffset() {
    return 0
  }
}

const jakartaPartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: JAKARTA_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

export interface JakartaParts {
  year: number
  /** 1-12, as a human writes it. */
  month: number
  day: number
  hour: number
  minute: number
  second: number
  millisecond: number
}

/** The Jakarta wall-clock fields of a real instant. */
function partsOfInstant(instant: Date): JakartaParts | null {
  const parts = jakartaPartsFormatter.formatToParts(instant)
  const num = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((p) => p.type === type)
    return part ? Number(part.value) : NaN
  }
  const year = num('year')
  const month = num('month')
  const day = num('day')
  // Some engines render midnight as hour 24 under hour12:false.
  const rawHour = num('hour')
  const hour = rawHour === 24 ? 0 : rawHour
  const minute = num('minute')
  const second = num('second')
  if ([year, month, day, hour, minute, second].some((n) => Number.isNaN(n))) return null
  return {
    year,
    month,
    day,
    hour,
    minute,
    second,
    // Jakarta is a whole-minute offset, so sub-minute fields are zone-invariant.
    millisecond: instant.getUTCMilliseconds(),
  }
}

/**
 * The Jakarta wall-clock fields of any accepted value, or `null` when the
 * value is missing or unparseable. A bare `YYYY-MM-DD` is read as that literal
 * calendar date at 00:00, never shifted.
 */
export function jakartaParts(value: DateTimeValue): JakartaParts | null {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'string') {
    const dateOnly = DATE_ONLY_RE.exec(value)
    if (dateOnly) {
      return {
        year: Number(dateOnly[1]),
        month: Number(dateOnly[2]),
        day: Number(dateOnly[3]),
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      }
    }
  }

  const instant = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(instant.getTime())) return null
  return partsOfInstant(instant)
}

function wallClockFromParts(parts: JakartaParts): JakartaWallClock {
  return new JakartaWallClock(
    Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
      parts.millisecond,
    ),
  )
}

/**
 * The value as a Jakarta wall clock ready for date-fns, or `null` when it is
 * missing or unparseable. Display only — see {@link JakartaWallClock}.
 */
export function toJakarta(value: DateTimeValue): Date | null {
  const parts = jakartaParts(value)
  return parts ? wallClockFromParts(parts) : null
}

/**
 * Render a date or time in Jakarta time. `pattern` is a date-fns `format`
 * pattern, so a call site keeps the pattern string it already had. Missing or
 * unparseable values return `fallback` (default `''`) instead of throwing, so
 * a caller that guarded with `?? '—'` keeps showing its dash.
 */
export function formatJakarta(
  value: DateTimeValue,
  pattern: string,
  fallback = '',
): string {
  const wallClock = toJakarta(value)
  if (!wallClock) return fallback
  return formatDateFns(wallClock, pattern)
}

/**
 * Render a date or time in Jakarta time using `Intl` options and an explicit
 * BCP-47 locale. For the places that deliberately follow the app's `en`/`id`
 * choice rather than the house `dd MMM yyyy` pattern — the PDF and XLSX
 * generators. Same value semantics as {@link formatJakarta}: a bare
 * `YYYY-MM-DD` stays that calendar date, an instant is shown in Jakarta.
 */
export function formatJakartaLocalized(
  value: DateTimeValue,
  locale: string,
  options: Intl.DateTimeFormatOptions,
  fallback = '',
): string {
  const parts = jakartaParts(value)
  if (!parts) return fallback
  // The fields are already Jakarta, so render them as-is by reading them back
  // out of UTC — formatting in 'Asia/Jakarta' here would shift them a second time.
  const asUtc = new Date(
    Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
      parts.millisecond,
    ),
  )
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(asUtc)
}

/** "Now" as a Jakarta wall clock — for `formatJakarta(jakartaNow(), …)`. */
export function jakartaNow(): Date {
  return toJakarta(new Date()) as Date
}

/** Today's Jakarta calendar date as `YYYY-MM-DD`. */
export function jakartaToday(): string {
  return formatJakarta(new Date(), 'yyyy-MM-dd')
}

/**
 * The real UTC instant of midnight at the start of a value's Jakarta calendar
 * day (default: today). This is the anchor for comparing against bare
 * `YYYY-MM-DD` business dates, because `new Date('2027-04-30')` parses to UTC
 * midnight — so both sides of the comparison sit on the same footing and a
 * viewer's own zone never moves the boundary.
 */
export function jakartaDayStartUtc(value: DateTimeValue = new Date()): Date {
  const parts = jakartaParts(value)
  if (!parts) return new Date(NaN)
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day))
}

/**
 * Add calendar months to an instant using its UTC fields, so a boundary built
 * by {@link jakartaDayStartUtc} is not dragged around by the viewer's zone the
 * way `setMonth` would do.
 */
export function addUtcMonths(date: Date, months: number): Date {
  const next = new Date(date.getTime())
  next.setUTCMonth(next.getUTCMonth() + months)
  return next
}
